import DownloadIcon from "@mixitone/icons/download.svg";
import { Button, Modal, ModalFooter, Spinner } from "@mixitone/components";
import type { ClubPrintPaletteId } from "@mixitone/models";
import { savePdfBlob } from "lib/clubDirectoryPdf";
import { PRINT_PALETTES, paletteSwatch } from "lib/clubDirectoryPrintPalettes";
import { useLayoutEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

export type ClubDirectoryPdfPreview = {
  kind: "flyer" | "card";
  blob: Blob;
  filename: string;
};

type Props = {
  preview: ClubDirectoryPdfPreview;
  paletteId: ClubPrintPaletteId;
  regenerating?: boolean;
  onPaletteChange: (id: ClubPrintPaletteId) => void;
  onClose: () => void;
};

const A4_RATIO = 210 / 297;

const ClubDirectoryPdfPreviewModal: React.FC<Props> = ({
  preview,
  paletteId,
  regenerating,
  onPaletteChange,
  onClose,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(400);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const update = () => {
      const maxWidth = el.clientWidth;
      const maxHeight = Math.min(window.innerHeight * 0.58, 680);
      if (preview.kind === "card") {
        setWidth(Math.min(maxWidth, 560));
      } else {
        setWidth(Math.min(maxWidth, maxHeight * A4_RATIO));
      }
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [preview.kind]);

  return (
    <Modal
      title={preview.kind === "flyer" ? "Club flyer" : "Club card"}
      onClose={onClose}
      portal
      className="w-full max-w-3xl"
    >
      <div className="mb-3">
        <p className="text-sm font-medium text-slate-800">Colours</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {PRINT_PALETTES.map((palette) => {
            const swatch = paletteSwatch(palette);
            const selected = palette.id === paletteId;
            return (
              <button
                key={palette.id}
                type="button"
                title={palette.label}
                aria-pressed={selected}
                onClick={() => onPaletteChange(palette.id)}
                className={`flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm ${
                  selected
                    ? "border-slate-900 bg-white ring-2 ring-slate-900/15"
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
              >
                <span className="flex h-5 w-8 overflow-hidden rounded-sm ring-1 ring-black/10">
                  <span className="w-1/2" style={{ backgroundColor: swatch.ink }} />
                  <span className="w-1/2" style={{ backgroundColor: swatch.accent }} />
                </span>
                {palette.label}
              </button>
            );
          })}
        </div>
      </div>
      <div ref={wrapRef} className="relative flex justify-center overflow-hidden rounded-md bg-slate-100 p-3">
        <Document key={preview.filename + paletteId} file={preview.blob} loading={<Spinner size={48} />}>
          <Page pageNumber={1} width={width} renderTextLayer={false} renderAnnotationLayer={false} />
        </Document>
        {regenerating && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/60">
            <Spinner size={40} />
          </div>
        )}
      </div>
      <ModalFooter>
        <Button kind="secondary" onClick={onClose}>
          Close
        </Button>
        <Button onClick={() => savePdfBlob(preview.blob, preview.filename)} disabled={regenerating}>
          <DownloadIcon className="h-4 w-4 shrink-0" aria-hidden />
          Download PDF
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ClubDirectoryPdfPreviewModal;
